// Client-side photo quality advisory. Analyzes images picked in any portal
// file input (camera, drag-drop picker, per-item pages) on a downscaled
// canvas and reveals a non-blocking "retake?" banner when the photo looks
// blurry, too dark, or washed out by glare. The upload itself is never
// blocked — LiveView auto_upload proceeds in parallel.
//
// Thresholds are tuned on ~512px downscales; see analyzeImage().
const MAX_DIM = 512;
const BLUR_VARIANCE_MIN = 60; // Laplacian variance below this → blurry
const DARK_LUMA_MAX = 60; // mean luminance (0-255) below this → too dark
const GLARE_CLIPPED_FRACTION = 0.05; // >5% pixels at ≥250 luma → glare

const ISSUE_KEYS = ["blurry", "too_dark", "glare"];

async function analyzeImage(file) {
  if (!file.type.startsWith("image/")) return null;

  let bitmap;
  try {
    // HEIC (and anything else the browser can't decode) rejects here; the
    // server-side AI check still covers those files.
    //
    // Large files (camera photos) are downscaled by the decoder itself so we
    // never hold a full-resolution bitmap; small files decode as-is, since
    // resizeWidth would upscale them and soften the blur metric.
    const opts =
      file.size > 300 * 1024
        ? { resizeWidth: MAX_DIM, resizeQuality: "high" }
        : {};
    bitmap = await createImageBitmap(file, opts);
  } catch (_e) {
    return null;
  }

  const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close && bitmap.close();

  let data;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch (_e) {
    return null;
  }

  // Grayscale (luma fits 0-255; float precision doesn't move the thresholds)
  const gray = new Uint8ClampedArray(w * h);
  let lumaSum = 0;
  let clipped = 0;
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const l = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    gray[p] = l;
    lumaSum += l;
    if (l >= 250) clipped++;
  }
  const n = w * h;
  const meanLuma = lumaSum / n;

  // Laplacian variance (4-neighbor kernel) for focus measure
  let lapSum = 0;
  let lapSqSum = 0;
  let lapCount = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const p = y * w + x;
      const v =
        4 * gray[p] - gray[p - 1] - gray[p + 1] - gray[p - w] - gray[p + w];
      lapSum += v;
      lapSqSum += v * v;
      lapCount++;
    }
  }
  const lapMean = lapSum / lapCount;
  const lapVariance = lapSqSum / lapCount - lapMean * lapMean;

  const issues = [];
  if (lapVariance < BLUR_VARIANCE_MIN) issues.push("blurry");
  if (meanLuma < DARK_LUMA_MAX) issues.push("too_dark");
  if (clipped / n > GLARE_CLIPPED_FRACTION) issues.push("glare");
  return issues;
}

const PhotoQualityCheck = {
  mounted() {
    // Synchronous guard first: this fires for every change event on the page,
    // so non-file inputs must bail before any async work is scheduled.
    this.handler = (e) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement) || input.type !== "file") return;
      if (!input.files || input.files.length === 0) return;

      this.lastInput = input;
      // Analyze at most a few files; decodes overlap, the pixel loops don't.
      const files = Array.from(input.files).slice(0, 5);
      Promise.all(files.map((file) => analyzeImage(file))).then((results) => {
        const findings = files
          .map((file, i) => ({ file, issues: results[i] }))
          .filter((f) => f.issues && f.issues.length > 0);
        if (findings.length > 0) this.showWarning(findings);
      });
    };
    document.addEventListener("change", this.handler, true);

    this.el.querySelector("[data-quality-retake]").addEventListener("click", () => {
      this.hideWarning();
      if (this.lastInput) this.lastInput.click();
    });
    this.el.querySelector("[data-quality-dismiss]").addEventListener("click", () => {
      this.hideWarning();
    });
  },

  destroyed() {
    document.removeEventListener("change", this.handler, true);
  },

  showWarning(findings) {
    const issues = new Set(findings.flatMap((f) => f.issues));
    ISSUE_KEYS.forEach((key) => {
      const el = this.el.querySelector(`[data-quality-issue="${key}"]`);
      if (el) el.classList.toggle("hidden", !issues.has(key));
    });
    const nameEl = this.el.querySelector("[data-quality-filename]");
    if (nameEl) nameEl.textContent = findings.map((f) => f.file.name).join(", ");
    this.el.classList.remove("hidden");
    this.el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  },

  hideWarning() {
    this.el.classList.add("hidden");
  },
};

export default PhotoQualityCheck;
